/*
 * BeGrid -- THE TILING GRID, and the one that reflows.
 *
 * The two grids answer different questions, and this is the one to reach for
 * when a thing should KEEP ITS SIZE rather than share the space:
 *
 *   <BeCols>  divide the row into 16 and place cells by span. A cell is a
 *             FRACTION, so it narrows with the container and every row stacks
 *             at one threshold. Tracks stay integral.
 *   <BeGrid>  tile items at a MINIMUM size; the count is whatever fits, and the
 *             row reflows when one no longer does. No threshold involved.
 *
 * `min` IS A COUNT OF PAGE UNITS, not pixels. It was `track`, taking raw px --
 * the last measurement anyone had to know to use this layer, and the thing that
 * made `track={300}` feel arbitrary at every call site. 300px is "about 19
 * units"; `min={20}` says the same in the vocabulary the rest of the system
 * uses, and cannot be off-rhythm.
 *
 * `cols` -- a fixed count with FRACTIONAL tracks -- was deleted 2026-08-13: zero
 * call sites, bound to no note, and a mode producing fractional tracks by design
 * contradicts the layer it lives in. `rigid` STAYS: Notes/69 §2 makes it the
 * integrality escape hatch that justified shipping `auto-fit` at all.
 */

import type { CSSProperties, ElementType, HTMLAttributes, ReactNode } from "react";
import { type BeCommon, beComponent } from "./create.tsx";
import { type BeSpace, space } from "./Stack.tsx";

export type BeGridProps = BeCommon &
	Omit<HTMLAttributes<HTMLElement>, "className" | "style"> & {
		children?: ReactNode;
		gap?: BeSpace;
		/** Block-axis alignment of the cells. Grid's own default is "stretch";
		 * "baseline" aligns the cells' text lines instead of their boxes. */
		align?: "start" | "center" | "end" | "stretch" | "baseline";
		/**
		 * Minimum tile width, IN PAGE UNITS. The count is whatever fits at that
		 * size, so items keep their size and the row reflows instead of every item
		 * narrowing together. Default 15 (240px).
		 */
		min?: number;
		/** Exact tracks, leaving a hole rather than growing survivors. The
		 * integrality opt-out -- Notes/69 §2. */
		rigid?: boolean;
		/** One row that overflows rather than wrapping, for tracks whose ORDER
		 * carries meaning. Needs a <BeScrollView horizontal> around it. */
		nowrap?: boolean;
		/** Inset on all sides. The CONTAINER owns padding, so no child carries one. */
		pad?: BeSpace;
		subgrid?: boolean;
		as?: ElementType;
	};

export const BeGrid = beComponent<BeGridProps, HTMLElement>("BeGrid", (props, be) => {
	const { children, gap, pad, align, min, subgrid, rigid, nowrap, as: Tag = "div", scale, className, style, ...rest } = props;

	const vars: Record<string, string> = {};
	if (gap) vars["--be-gap"] = space(gap);
	if (pad) vars["--be-pad"] = space(pad);
	if (min != null) vars["--be-grid-track"] = `calc(${Math.max(1, Math.round(min))} * var(--be-page-unit))`;

	const root = be(
		"be-grid",
		align && align !== "stretch" && `is-align-${align}`,
		rigid && "is-rigid",
		nowrap && "is-nowrap",
		subgrid && "is-subgrid",
	);

	return (
		<Tag {...rest} {...root} style={{ ...root.style, ...vars } as CSSProperties}>
			{children}
		</Tag>
	);
});
